import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AuthStatus } from '@pipyar/core';
import { ApiRequestError } from './api';
import { authApi } from './auth';

export const AUTH_STATUS_KEY = ['auth', 'status'] as const;

/**
 * The single source of truth for "am I signed in".
 *
 * Every screen reads this query, so a login or logout anywhere invalidates one key
 * and the whole app re-renders consistently.
 */
export function useAuthStatus() {
  return useQuery<AuthStatus>({
    queryKey: AUTH_STATUS_KEY,
    queryFn: authApi.status,
    // Auth state changes only as a result of an action we take, so no polling.
    staleTime: 30_000,
    /*
     * Retry a dropped connection, but never a real answer.
     *
     * The global default is retry:false because a 401 or a validation error
     * will not fix itself. A network failure is the opposite case: a laptop
     * waking, a wifi handover or a moment of packet loss makes ONE request
     * fail, and with no retry that single blip parked the whole app on
     * "Cannot reach the server" until it was manually reloaded — while the
     * server had never returned anything but 200.
     */
    retry: (failureCount, error) => {
      const status = error instanceof ApiRequestError ? error.status : 0;
      // 0 is our marker for "the fetch itself was rejected".
      if (status >= 400 && status < 500) return false;
      return failureCount < 3;
    },
    retryDelay: (attempt) => Math.min(500 * 2 ** attempt, 4_000),
    refetchOnWindowFocus: true,
    // Coming back from offline is exactly when this needs to re-ask.
    refetchOnReconnect: true,
  });
}

export function useLogout() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: authApi.logout,
    onSuccess: async () => {
      // Drop every cached query, not just auth: anything fetched while signed in
      // belongs to that session and must not leak into the next one.
      queryClient.clear();
      await queryClient.invalidateQueries({ queryKey: AUTH_STATUS_KEY });
    },
  });
}
